import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import {
  clearPersistedState,
  loadPersistedState,
  savePersistedState,
} from './signal-store-persist.util';
import type {
  IngestProject,
  IngestSubfolder,
} from 'libs/vault/src/lib/domain/src/lib/ingest/ingest-project.model';
import type { IngestProjectDetailState } from 'libs/vault/src/lib/domain/src/lib/ingest/ingest-store.model';

const STORAGE_KEY = 'ingest_project_detail_state';
const STORAGE_VERSION = 1;

type IngestProjectDetailPersistedState = Pick<
  IngestProjectDetailState,
  'project' | 'subfolders'
>;

const persistConfig = {
  key: STORAGE_KEY,
  version: STORAGE_VERSION,
  storage: 'session' as const,
};

const initialState: IngestProjectDetailState = {
  project: null,
  loading: true,
  error: null,
  subfolders: [],
  loadingSubfolders: false,
  subfoldersError: null,
  creatingSubfolder: false,
};

function isIngestProjectDetailPersistedState(
  value: unknown,
): value is IngestProjectDetailPersistedState {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  const project = v['project'];
  const subfolders = v['subfolders'];
  const projectOk = project === null || typeof project === 'object';
  return projectOk && Array.isArray(subfolders);
}

const persistedInitialState: IngestProjectDetailState = {
  ...initialState,
  ...(loadPersistedState(
    persistConfig,
    isIngestProjectDetailPersistedState,
  ) ?? {}),
  loading: true,
  loadingSubfolders: false,
  creatingSubfolder: false,
  error: null,
  subfoldersError: null,
};

export const ingestProjectDetailStore = signalStore(
  { providedIn: 'root' },
  withState(persistedInitialState),
  withMethods((store) => ({
    resetForNavigation(): void {
      patchState(store, {
        ...initialState,
        loading: true,
      });
    },

    setLoading(value: boolean): void {
      patchState(store, { loading: value });
    },

    setError(value: string | null): void {
      patchState(store, { error: value });
    },

    setProject(value: IngestProject | null): void {
      patchState(store, { project: value });
      saveDetailState(store.project(), store.subfolders());
    },

    setSubfolders(items: IngestSubfolder[]): void {
      patchState(store, { subfolders: items });
      saveDetailState(store.project(), items);
    },

    setLoadingSubfolders(value: boolean): void {
      patchState(store, { loadingSubfolders: value });
    },

    setSubfoldersError(value: string | null): void {
      patchState(store, { subfoldersError: value });
    },

    setCreatingSubfolder(value: boolean): void {
      patchState(store, { creatingSubfolder: value });
    },
    clearPersistedState(): void {
      clearPersistedState(persistConfig);
      patchState(store, initialState);
    },
  })),
);

function saveDetailState(
  project: IngestProject | null,
  subfolders: IngestSubfolder[],
): void {
  const stateToSave: IngestProjectDetailPersistedState = {
    project,
    subfolders,
  };
  savePersistedState(persistConfig, stateToSave);
}

export type IngestProjectDetailStoreType = typeof ingestProjectDetailStore;
